import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Container,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha
} from "@mui/material";
import {Pencil, Trash2, Plus, Undo2, AlertCircle} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {useConfirmDialog} from "../../hooks/use-confirm-dialog.ts";
import {useQueryClient} from "@tanstack/react-query";
import {
  useGetApprovalFlowsQuery,
  useDeleteApprovalFlowMutation,
  useRestoreApprovalFlowMutation,
  useSetOrganizationDefaultApprovalFlowMutation,
  ApprovalFlowFieldsFragment,
} from "../../graphql/graphql.ts";
import {LoadingPage} from "../LoadingPage.tsx";
import {useSetUserData, useUserData} from "../../hooks/use-user-data-provider.ts";
import {Permission} from "../../utils/permissions.ts";
import {NotFoundPage} from "../NotFoundPage.tsx";
import toast from "react-hot-toast";

export const ApprovalFlowListPage: React.FC = () => {
  const navigate = useNavigate();
  const {data, isLoading} = useGetApprovalFlowsQuery();
  const {mutateAsync: deleteApprovalFlow} = useDeleteApprovalFlowMutation();
  const {mutateAsync: restoreApprovalFlow} = useRestoreApprovalFlowMutation();
  const {mutateAsync: setOrganizationDefaultApprovalFlow} = useSetOrganizationDefaultApprovalFlowMutation();
  const queryClient = useQueryClient();
  const queryKey = useGetApprovalFlowsQuery.getKey();
  const {permissions, user} = useUserData();
  const setUserData = useSetUserData();
  const confirmDialog = useConfirmDialog();
  
  // 権限チェック
  if (!permissions.includes(Permission.WriteApprove)) {
    return <NotFoundPage/>;
  }
  
  const handleEdit = (id: string) => {
    navigate(`/admin/approvalFlow/${id}`);
  };
  
  const handleRestore = async (id: string, name: string) => {
    confirmDialog.open({
      fullWidth: true,
      icon: <Undo2 color="primary"/>,
      title: (
        <Typography
          sx={{color: "primary.contrastText"}}
          fontWeight={"bold"}
        >
          承認フローの復元
        </Typography>
      ),
      children: (
        <Stack direction={"column"} spacing={2} minWidth={"250px"}>
          <Typography>{`${name}を復元してもよろしいですか？`}</Typography>
          <Stack direction={"row"} spacing={2}>
            <Button
              fullWidth
              variant={"outlined"}
              onClick={async () => confirmDialog.close()}
            >
              キャンセル
            </Button>
            <Button
              fullWidth
              variant={"contained"}
              onClick={async () => {
                try {
                  const result = await restoreApprovalFlow({
                    id,
                  });
                  
                  if (result?.restoreApprovalFlow) {
                    // キャッシュを更新
                    queryClient.setQueryData(
                      queryKey,
                      (oldData: any) => ({
                        getApprovalFlows: oldData.getApprovalFlows.map((flow: ApprovalFlowFieldsFragment) =>
                          flow.id === id ? {...flow, isDeleted: false} : flow
                        ),
                      }),
                    );
                    toast.success("承認フローを復元しました");
                    confirmDialog.close();
                  }
                } catch (error) {
                  console.error("Failed to restore approval flow:", error);
                  toast.error("承認フローの復元に失敗しました");
                }
              }}
            >
              復元する
            </Button>
          </Stack>
        </Stack>
      ),
    });
  };
  
  const handleDelete = async (id: string, name: string) => {
    confirmDialog.open({
      fullWidth: true,
      icon: <Trash2 color="error"/>,
      title: (
        <Typography
          sx={{color: "primary.contrastText"}}
          fontWeight={"bold"}
        >
          承認フローの削除
        </Typography>
      ),
      children: (
        <Stack direction={"column"} spacing={2} minWidth={"250px"}>
          <Typography>{`${name}を削除してもよろしいですか？`}</Typography>
          <Stack direction={"row"} spacing={2}>
            <Button
              fullWidth
              variant={"outlined"}
              onClick={async () => confirmDialog.close()}
            >
              キャンセル
            </Button>
            <Button
              fullWidth
              variant={"contained"}
              color="error"
              onClick={async () => {
                try {
                  const result = await deleteApprovalFlow({
                    id,
                  });
                  
                  if (result?.deleteApprovalFlow) {
                    // キャッシュを更新
                    queryClient.setQueryData(
                      queryKey,
                      (oldData: any) => ({
                        getApprovalFlows: oldData.getApprovalFlows.map((flow: ApprovalFlowFieldsFragment) =>
                          flow.id === id ? {...flow, isDeleted: true} : flow
                        ),
                      }),
                    );
                    toast.success("承認フローを削除しました");
                    confirmDialog.close();
                  }
                } catch (error) {
                  console.error("Failed to delete approval flow:", error);
                  toast.error("承認フローの削除に失敗しました");
                }
              }}
            >
              削除する
            </Button>
          </Stack>
        </Stack>
      ),
    });
  };
  
  const handleSetDefault = async (id: string | null) => {
    try {
      await setOrganizationDefaultApprovalFlow({
        id,
      });
      // ユーザーデータを更新
      if (user) {
        setUserData({
          isLoading: false,
          user: {
            ...user,
            organization: user.organization ? {
              ...user.organization,
              defaultApprovalFlowId: id,
            } : null,
          },
          permissions,
        });
      }
      toast.success(id ? "デフォルトに設定しました" : "デフォルト設定を解除しました");
    } catch (error) {
      console.error("Failed to set default approval flow:", error);
      toast.error("デフォルト設定の更新に失敗しました");
    }
  };
  
  if (isLoading) {
    return <LoadingPage/>;
  }
  
  return (
    <Container maxWidth="lg" sx={{py: 4}}>
      <Box sx={{maxWidth: 1400, margin: '0 auto'}}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
            }}
          >
            承認フロー管理
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/admin/approvalFlow/new")}
            startIcon={<Plus size={20}/>}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            新規作成
          </Button>
        </Box>
        
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 3,
            borderRadius: 2,
            overflow: 'hidden',
            '& .MuiTableCell-root': {
              borderColor: 'rgba(224, 224, 224, 0.4)',
            },
          }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    py: 2,
                    width: "120px",
                    '&:first-of-type': {
                      borderTopLeftRadius: 8,
                    },
                  }}
                >
                  組織デフォルト
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    py: 2,
                  }}
                >
                  名前
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    py: 2,
                  }}
                >
                  説明
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    py: 2,
                    width: "120px",
                  }}
                >
                  ステップ数
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    py: 2,
                    width: "120px",
                    '&:last-child': {
                      borderTopRightRadius: 8,
                    },
                  }}
                  align="center"
                >
                  操作
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.getApprovalFlows.map((flow) => (
                <TableRow
                  key={flow.id}
                  sx={{
                    bgcolor: flow.isDeleted ? "rgba(0, 0, 0, 0.05)" : "inherit",
                    '&:hover': {
                      bgcolor: flow.isDeleted ? "rgba(0, 0, 0, 0.08)" : "rgba(0, 0, 0, 0.03)",
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                  hover
                >
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Checkbox
                        checked={user?.organization?.defaultApprovalFlowId === flow.id}
                        onChange={(e) => handleSetDefault(e.target.checked ? flow.id : null)}
                        disabled={flow.isDeleted}
                        sx={{
                          color: flow.isDeleted ? 'text.disabled' : 'primary.main',
                          '&.Mui-checked': {
                            color: flow.isDeleted ? 'text.disabled' : 'primary.main',
                          },
                        }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {flow.isDeleted && (
                        <AlertCircle size={16} color="#f44336"/>
                      )}
                      <Typography
                        sx={{
                          textDecoration: flow.isDeleted ? 'line-through' : 'none',
                          color: flow.isDeleted ? 'text.secondary' : 'text.primary'
                        }}
                      >
                        {flow.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        color: flow.isDeleted ? 'text.secondary' : 'text.primary'
                      }}
                    >
                      {flow.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        color: flow.isDeleted ? 'text.secondary' : 'text.primary'
                      }}
                    >
                      {flow.steps.filter((s) => !s.isDeleted).length}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {flow.isDeleted ? (
                        <IconButton
                          onClick={() => handleRestore(flow.id, flow.name)}
                          size="small"
                          sx={{
                            color: 'primary.main',
                            backgroundColor: alpha('#1976d2', 0.04),
                            border: '1px solid',
                            borderColor: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <Undo2 size={18}/>
                        </IconButton>
                      ) : (
                        <>
                          <IconButton
                            onClick={() => handleEdit(flow.id)}
                            size="small"
                            sx={{
                              color: 'primary.main',
                              backgroundColor: alpha('#1976d2', 0.04),
                              border: '1px solid',
                              borderColor: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white',
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <Pencil size={18}/>
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(flow.id, flow.name)}
                            size="small"
                            disabled={user?.organization?.defaultApprovalFlowId === flow.id}
                            sx={{
                              color: 'error.main',
                              backgroundColor: alpha('#d32f2f', 0.04),
                              border: '1px solid',
                              borderColor: 'error.main',
                              '&:hover': {
                                backgroundColor: 'error.main',
                                color: 'white',
                              },
                              transition: 'all 0.2s ease',
                              '&.Mui-disabled': {
                                opacity: 0.5,
                                borderColor: 'text.disabled',
                              }
                            }}
                          >
                            <Trash2 size={18}/>
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {(!data?.getApprovalFlows || data.getApprovalFlows.length === 0) && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="textSecondary">
                      承認フローが存在しません
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};

export default ApprovalFlowListPage;
